import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { marked, Tokens, type Token } from 'marked'

export class AiMarkdownUtil {
  private renderAuctionUrlName = ''
  private insideLink = false

  constructor(
    private domSanitizer: DomSanitizer,
    private router: Router,
  ) {}

  renderMarkdown(content: string, currentAuctionUrlName: string): SafeHtml {
    this.renderAuctionUrlName = currentAuctionUrlName
    const renderer = new marked.Renderer()
    renderer.text = (token: Tokens.Text) => {
      if (token.tokens?.length) {
        return this.renderMarkdownTokens(token.tokens)
      }
      const text = token.text
      return this.insideLink ? this.escapeHtml(text) : this.linkifyGalabidSupport(this.escapeHtml(text))
    }
    renderer.link = ({ href, tokens }: Tokens.Link) => {
      this.insideLink = true
      const text = this.renderMarkdownTokens(tokens)
      this.insideLink = false
      const linkHref = this.escapeHtml(href ?? '')
      return `<a href="${linkHref}"${this.getMarkdownLinkTargetAttributes(linkHref)}>${text}</a>`
    }
    const html = marked.parse(content, { async: false, renderer }) as string
    return this.domSanitizer.bypassSecurityTrustHtml(html)
  }

  onMarkdownLinkClick(event: MouseEvent, currentAuctionUrlName: string): void {
    const targetElement = event.target

    if (!(targetElement instanceof Element)) {
      return
    }

    const anchor = targetElement.closest('a')
    if (!(anchor instanceof HTMLAnchorElement)) {
      return
    }

    const href = anchor.getAttribute('href') || ''
    if (!href || !this.isDashboardCampaignRoute(href)) {
      return
    }

    const routePath = this.getDashboardRoutePath(href)
    if (!routePath) {
      return
    }

    event.preventDefault()
    this.router.navigateByUrl(routePath)
  }

  private renderMarkdownTokens(tokens: Token[]): string {
    return tokens.map((token) => this.renderMarkdownToken(token)).join('')
  }

  private renderMarkdownToken(token: Token): string {
    switch (token.type) {
      case 'text': {
        const textContent = (token as Tokens.Text).text
        return this.insideLink ? this.escapeHtml(textContent) : this.linkifyGalabidSupport(this.escapeHtml(textContent))
      }
      case 'strong':
        return `<strong>${this.renderMarkdownTokens((token as Tokens.Strong).tokens)}</strong>`
      case 'em':
        return `<em>${this.renderMarkdownTokens((token as Tokens.Em).tokens)}</em>`
      case 'codespan':
        return `<code>${this.escapeHtml((token as Tokens.Codespan).text)}</code>`
      case 'del':
        return `<del>${this.renderMarkdownTokens((token as Tokens.Del).tokens)}</del>`
      case 'br':
        return '<br>'
      case 'link':
        return this.rendererLink(token as Tokens.Link)
      default:
        return 'text' in token ? this.escapeHtml(String((token as { text: string }).text)) : ''
    }
  }

  private rendererLink(token: Tokens.Link): string {
    this.insideLink = true
    const text = this.renderMarkdownTokens(token.tokens)
    this.insideLink = false
    const linkHref = this.escapeHtml(token.href ?? '')
    return `<a href="${linkHref}"${this.getMarkdownLinkTargetAttributes(linkHref)}>${text}</a>`
  }

  private getMarkdownLinkTargetAttributes(href: string): string {
    if (!this.shouldOpenMarkdownLinkInNewTab(href)) {
      return ''
    }

    return ' target="_blank" rel="noopener noreferrer"'
  }

  private shouldOpenMarkdownLinkInNewTab(href: string): boolean {
    if (!href) {
      return false
    }

    return !this.isDashboardCampaignRoute(href)
  }

  private isDashboardCampaignRoute(href: string): boolean {
    if (!this.renderAuctionUrlName) {
      return false
    }

    const campaignRoute = `/campaign/${this.renderAuctionUrlName}`

    try {
      const parsedUrl = new URL(href, window.location.origin)
      return parsedUrl.pathname.includes(campaignRoute)
    } catch {
      return href.includes(campaignRoute)
    }
  }

  private escapeHtml(text: string): string {
    return text.replace(/[&<>"']/g, (char) => {
      const entities: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
      return entities[char]
    })
  }

  private linkifyGalabidSupport(text: string): string {
    return text.replace(/\b(galabid support)\b/gi, '<a href="#galabid-support" class="ai-chat-support-link">$1</a>')
  }

  private getDashboardRoutePath(href: string): string | null {
    try {
      const parsedUrl = new URL(href, window.location.origin)
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
    } catch {
      if (href.startsWith('/')) {
        return href
      }

      return null
    }
  }
}
